import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchApplications } from "../../../store/reducers/applicationSlice";
import { handleFilesDownload } from "../../../store/reducers/fileSlice";
import Navbar from "../../navbar";
import Header from "../../header";
import Pagination from "../dashboard/appPagination";
import iconSearch from "../../../assets/media/icon-search.svg";
import DatePicker from "react-datepicker";

import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";

const columnHelper = createColumnHelper();

const PendingApp = () => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const authToken = user.access_token;

  const applications = useSelector((state) => state.applications);
  const getallUserdata = useSelector((state) => state.userdata.getalluserdata);

  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [filteredApplication, setFilteredApplication] = useState([]);
  const [downloadingIds, setDownloadingIds] = useState([]);
  const [dateRange, setDateRange] = useState([null, null]);
  const [startDate, endDate] = dateRange;

  // Fetch applications when date filter changes
  useEffect(() => {
    let queryParams = {};
    if (startDate && endDate) {
      queryParams.start_date = startDate.toISOString();
      queryParams.end_date = endDate.toISOString();
    }
    dispatch(fetchApplications({
      accessToken: authToken,
      queryParams,
      fetchAllPages: true // Fetch all pages to show all applications
    }));
  }, [authToken, dispatch, dateRange]);

  // Filter applications (pending_from_dw)
  useEffect(() => {
    if (applications.data?.length > 0 && getallUserdata?.length > 0) {
      const filteredApplications = applications.data.filter((item) => {
        const userExists = getallUserdata.some(
          (userdata) =>
            item.state === "pending_from_dw" &&
            userdata.company_id === user.user.company_id
        );
        return userExists;
      });

      const searched = filteredApplications.filter((item) =>
        Object.values(item).some(
          (value) =>
            value &&
            value.toString().toLowerCase().includes(searchQuery.toLowerCase())
        )
      );

      setFilteredApplication(searched);
    }
  }, [searchQuery, applications.data, getallUserdata, user.user.company_id]);

  const handleSearchInputChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const getTotalPages = () => {
    return Math.ceil(filteredApplication.length / 10);
  };

  const handleDownload = async (item) => {
    const { id: applicationId, user_id: userId } = item;

    setDownloadingIds((prev) => [...prev, applicationId]);

    try {
      await dispatch(handleFilesDownload({ userId, applicationId, authToken }));
    } catch (error) {
      console.error("Download failed", error);
    } finally {
      setDownloadingIds((prev) => prev.filter((id) => id !== applicationId));
    }
  };

  // Pagination + sorting data
  const paginatedData = useMemo(() => {
    return filteredApplication
      .sort((a, b) => b.id - a.id)
      .slice((currentPage - 1) * 10, currentPage * 10);
  }, [filteredApplication, currentPage]);

  // React Table columns
  const columns = useMemo(
    () => [
      columnHelper.display({
        id: "serial",
        header: "S.NO.",
        cell: (info) => info.row.index + 1,
      }),
      columnHelper.accessor("id", {
        header: "Application No.",
        cell: (info) => `GB-${info.getValue()}`,
      }),
      columnHelper.display({
        id: "applicationName",
        header: "Application Name",
        cell: (info) => {
          const row = info.row.original;
          return (
            <div className="boardTitle">
              <strong>{row.name}</strong>
              <span>{row.category}</span>
            </div>
          );
        },
      }),
      columnHelper.display({
        id: "submittedBy",
        header: "Submitted By",
        cell: (info) => {
          const row = info.row.original;
          return (
          <div className="boardTitle">
            <strong>{row.companyName || row.company_name || 'N/A'}</strong>
            <span>{row.companyEmail || row.email || 'N/A'}</span>
          </div>
        );
      },
    }),
      columnHelper.accessor("updated_at", {
        header: "Submitted Date",
        cell: (info) => (
          <span className="dateText">{info.getValue()?.slice(0, 10)}</span>
        ),
      }),
      columnHelper.display({
        id: "downloads",
        header: "Downloads",
        cell: (info) => {
          const row = info.row.original;
          return (
            <div className="boardActions">
              {downloadingIds.includes(row.id) ? (
                <div className="loader"></div>
              ) : (
                <button
                  className="btn btnDownload"
                  type="button"
                  onClick={() => handleDownload(row)}
                >
                  Download
                </button>
              )}
            </div>
          );
        },
      }),
    ],
    [downloadingIds]
  );

  const table = useReactTable({
    data: paginatedData,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="mainApp">
      <Navbar />
      <Header />
      <div className="wrapper">
        <div className="boxAppBoard boxWhite">
          {/* Header with search + date filter */}
          <div className="boardHeader">
            <div className="boardHeadLeft">
              <h2>Pending Applications</h2>
            </div>
            <div className="boardHeadRight">
              <form className="searchForm">
                <input
                  type="text"
                  placeholder="Search"
                  onChange={handleSearchInputChange}
                  value={searchQuery}
                  className="searchInput"
                />
                <button type="button" className="searchBtn">
                  <img src={iconSearch} alt="search" />
                </button>
              </form>
              <div className="datePicker">
                <DatePicker
                  selectsRange={true}
                  startDate={startDate}
                  endDate={endDate}
                  onChange={(update) => setDateRange(update)}
                  isClearable={true}
                  placeholderText="Select Date Range"
                  className="date-range-input"
                  dateFormat="yyyy/MM/dd"
                />
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="boardContent table-responsive">
            <table className="table">
              <thead className="">
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr className="" key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th key={header.id} >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>

              <tbody>
                {table.getRowModel().rows.map((row) => (
                  <tr className="" key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <td
                        key={cell.id}
                        className={""}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>


          {/* Pagination */}
          <Pagination
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            totalPages={getTotalPages}
          />
        </div>
      </div>
    </div>
  );
};

export default PendingApp;
